import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { CardElement, useStripe, useElements } from "@stripe/react-stripe-js";
import toast from "react-hot-toast";
import * as request from "../../../services/FetchService";
import NewButton from "../../Buttons/NewButton";

const CARD_ELEMENT_OPTIONS = {
	hidePostalCode: true,
	iconStyle: "solid",
	disableLink: true,
	style: {
		base: {
			color: "#000000",
			iconColor: "rgb(108, 117, 125)",
			lineHeight: "28px",
			fontFamily: '"Roboto", Roboto, sans-serif',
			fontSmoothing: "antialiased",
			fontSize: "16px",
			"::placeholder": { color: "rgb(108, 117, 125)" },
		},
		invalid: { color: "#fa755a", iconColor: "#fa755a" },
	},
};

const AddCardNew = ({ getCards }) => {
	const { t } = useTranslation();
	const stripe = useStripe();
	const elements = useElements();

	const [loading, setLoading] = useState(false);
	const [cardholderName, setCardholderName] = useState("");

	const handleSubmit = async (event) => {
		event.preventDefault();
		if (!stripe || !elements || loading) return;
		setLoading(true);

		try {
			const cardElement = elements.getElement(CardElement);
			if (!cardElement) throw new Error("Card element not found");

			// Create PaymentMethod
			const pmRes = await stripe.createPaymentMethod({
				type: "card",
				card: cardElement,
				billing_details: { name: cardholderName || undefined },
			});

			if (pmRes.error) {
				toast.error(pmRes.error.message);
				return;
			}

			const paymentMethod = pmRes.paymentMethod;

			// Ask backend to create SetupIntent (or whatever your endpoint does)
			const createCardResponse = await request.postSubscription("", "payments/dataplan/cards/create", { payment_method: paymentMethod });

			if (createCardResponse.status !== 200) {
				toast.error(createCardResponse?.data?.message || "Failed to create card");
				return;
			}

			if (createCardResponse?.data?.status === "succeeded") {
				getCards?.();
				toast.success(t("toast.success"));
				return;
			}

			const clientSecret = createCardResponse.data.client_secret;
			if (!clientSecret) throw new Error("Missing client_secret from backend");


			// Confirm SetupIntent
			const confirmRes = await stripe.confirmCardSetup(clientSecret);


			if (confirmRes.error) {
				toast.error(confirmRes.error.message);
				return;
			}

			if (confirmRes.setupIntent?.status !== "succeeded") {
				toast.error("Card setup did not succeed.");
				return;
			}

			// Set primary on backend
			const primaryRes = await request.postSubscription("", "payments/dataplan/cards/confirm", { setup_intent_id: confirmRes.setupIntent?.id });

			if (primaryRes?.data?.successful) {
				getCards?.();
				toast.success(t("toast.success"));
			} else {
				toast.error(primaryRes?.data?.message || "Failed to set primary card");
			}
		} catch (err) {
			toast.error(err?.message || "Something went wrong");
		} finally {
			setLoading(false);
		}
	};

	return (
		<form onSubmit={handleSubmit}>
			<div className="form-group d-flex align-items-center">
				<input type="text" className="form-control rounded24 p-3 bg-light-grey grey-blue" placeholder={t("subscription.cardholder_name")} value={cardholderName} onChange={(e) => setCardholderName(e.target.value)} />
			</div>

			<div className="form-group mt-3">
				<CardElement className="form-control rounded24 p-3 bg-light-grey grey-blue" options={CARD_ELEMENT_OPTIONS} />
			</div>

			<NewButton className="bg-grey-blue white bold p-3 rounded24 mt-3 w-100" type="submit" disabled={!stripe || loading}>
				{t("subscription.add_card")}
				<span className={`${loading ? "" : "d-none"} ms-2 size_15 spinner-grow`} />
			</NewButton>
		</form>
	);
};

export default AddCardNew;
